/**
 * Dashboard quality slice schema (#1235, program #1130 E1)
 *
 * Zod contract for GET /api/v1/dashboard/quality
 * (apps/edge-api/src/routes/dashboard-quality.ts). One number with everything
 * needed to trust it: what it measures (`metric`, `metricVersion`), where it
 * was read (`source`), over which records (`window`, `scope`, `includeDemo`),
 * and on how many of them (`scoredCount`, `analysedCount`).
 *
 * Windows are half-open: current `[start, end)`, previous `[previousStart,
 * start)` of the same length — a record exactly at `start` belongs to the
 * current window only. The zone (`timeZone`, else `tzOffset`) affects only
 * how `days` are cut.
 *
 * The score is the canonical quality score (`computeCanonicalQualityScore` in
 * `@aica/core`) averaged over records that have one: a record without a score
 * is not a zero, so `score` is `null` when `scoredCount` is 0.
 */

import { z } from 'zod/v4';
import { dashboardPeriodSchema } from './dashboard';
import { REPORT_AGENT_MIN_SCORED_CALLS } from './reports';

// ============================================================================
// Query
// ============================================================================

/**
 * Query of GET /api/v1/dashboard/quality, less the project filter
 * (`projectIds` repeats in the URL — the route reads it with `getAll` through
 * `projectIdsFilterSchema`, like every other dashboard route) and `tzOffset`
 * (clamped, never rejected).
 *
 * `periodStart`/`periodEnd` are exact moments and override `period`.
 *
 * `timeZone` — the viewer's IANA zone. Days are cut by the offset it had at
 * each record's moment, so a daylight-saving change inside the window — any
 * number of them, at any hour — keeps every day on local midnight. An unknown
 * name falls back to the fixed `tzOffset`.
 */
export const dashboardQualityQuerySchema = z.object({
  period: dashboardPeriodSchema.default('week'),
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  includeDemo: z.enum(['yes', 'no']).default('yes'),
  timeZone: z.string().max(64).optional(),
});

/**
 * The quality slice's own query: `skills=1` — the page draws the skills, and
 * the route reads them (#2355). Without it the answer carries neither `skills`
 * nor `benchmark`. An edge deployed before it ignores the parameter and always
 * sends them. The other slices share the base schema, not this one.
 */
export const dashboardQualitySliceQuerySchema = dashboardQualityQuerySchema.extend({
  skills: z.literal('1').optional(),
});

// ============================================================================
// Response
// ============================================================================

/**
 * Nominally 0–100, but not clamped: sub-metrics are not range-checked, and the
 * canonical score keeps what they hold (the records list's score filter treats
 * `qaScoreMax=100` as "no bound" for the same reason, #1203).
 */
const scoreSchema = z.number();
const countSchema = z.number().int().nonnegative();

export const dashboardQualityScopeSchema = z.object({
  /**
   * `org` — every project of the org; `projects` — the listed projects;
   * `memberOf` — a caller who chose none and is bounded by their own
   * projects: a supervisor's membership NOW (live, not a list — it could be
   * stale and longer than a filter may carry), or a managed subject's
   * `projects` grant (E1.5); `mine` — the caller's own records (agent),
   * within `projectIds` when given; `people` — a group lead (E1.5
   * `departments` grant, PR 3b.4a): the records its people handled, within
   * `projectIds` when given. Its records link names only the chosen projects:
   * the records list holds a group lead to the same people itself (PR
   * 3b.4b-2).
   *
   * `people` is additive: a page deployed before it fails this enum. The route
   * answers it only to a `departments` subject, whom the capability gate
   * serves since PR 3b.4b-2 — after the page that knows it was live.
   */
  kind: z.enum(['org', 'projects', 'memberOf', 'mine', 'people']),
  /**
   * The chosen projects, after narrowing to those the caller may see; `null` —
   * none chosen. For `mine` under a `projects` grant with none chosen: the
   * grant's projects of this org, which the slice is within. For `people`: a
   * filter within the people, never their boundary.
   */
  projectIds: z.array(z.string()).nullable(),
  /**
   * `memberOf` only: the projects the slice counted, archived included — the
   * live membership read with the same predicate, or the grant's projects
   * that exist in this org — for a link to the records list, which takes a
   * list. `null` for any other kind. Optional so a page deployed ahead of the
   * route still reads the answer.
   */
  memberProjectIds: z.array(z.string()).nullable().optional(),
});

export const dashboardQualityDaySchema = z.object({
  /** Local calendar day `YYYY-MM-DD`, in the window's zone. */
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /**
   * The day as a half-open window `[start, end)`, clipped to the slice's own:
   * the very records counted under `day` — a link opens exactly these.
   */
  start: z.string().datetime(),
  end: z.string().datetime(),
  /** `null` — no scored record that day: an empty place, never a zero. */
  score: scoreSchema.nullable(),
  scoredCount: countSchema,
});

/**
 * The window a domain slice counted — one shape for every slice of the
 * unified dashboard (quality #1235, flow #1337): half-open, current
 * `[start, end)`, previous `[previousStart, start)`.
 */
export const dashboardSliceWindowSchema = z.object({
  start: z.string().datetime(),
  end: z.string().datetime(),
  previousStart: z.string().datetime(),
  /** The zone's offset at `start`, minutes east of UTC. */
  tzOffset: z.number().int(),
  /** The IANA zone the days were cut by; `null` — the fixed `tzOffset`. */
  timeZone: z.string().nullable(),
});

/**
 * The six skills (#1338 PR B) — the canonical score's sub-metrics, in the
 * order of `QUALITY_SUB_METRIC_KEYS` (`@aica/core`; api-types does not depend
 * on core, so the list is spelled here and a test pins the two equal).
 */
export const DASHBOARD_QUALITY_SKILL_KEYS = [
  'greetingScore',
  'needsScore',
  'clarityScore',
  'objectionScore',
  'closingScore',
  'politenessScore',
] as const;

/**
 * The category name of each skill — the one map from a sub-metric to its
 * label key (`categoryNames.<category>`), read by `/analytics/team-metrics`
 * and the dashboard's skills alike.
 */
export const QUALITY_SKILL_CATEGORY = {
  greetingScore: 'greeting',
  needsScore: 'discovery',
  clarityScore: 'solutionProposal',
  objectionScore: 'objectionHandling',
  closingScore: 'closing',
  politenessScore: 'toneEmpathy',
} as const satisfies Record<(typeof DASHBOARD_QUALITY_SKILL_KEYS)[number], string>;

/**
 * One skill over the slice's rows: the mean of that sub-metric where a row
 * carries it as a number (the canonical score's own rule), unweighted — the
 * project's weights belong to the overall score. `null` — no row carried it.
 */
export const dashboardQualitySkillSchema = z.object({
  key: z.enum(DASHBOARD_QUALITY_SKILL_KEYS),
  current: z.object({ score: scoreSchema.nullable(), scoredCount: countSchema }),
  previous: z.object({ score: scoreSchema.nullable(), scoredCount: countSchema }),
});

export const dashboardQualityResponseSchema = z.object({
  metric: z.literal('quality.canonical'),
  metricVersion: z.literal('submetric_mean_v1'),
  source: z.literal('d1'),
  /** When the numbers were read — every request is read fresh. */
  asOf: z.string().datetime(),
  window: dashboardSliceWindowSchema,
  scope: dashboardQualityScopeSchema,
  includeDemo: z.enum(['yes', 'no']),
  current: z.object({
    score: scoreSchema.nullable(),
    scoredCount: countSchema,
    /** Records with a current analysis; `analysedCount − scoredCount` are unscored. */
    analysedCount: countSchema,
  }),
  previous: z.object({
    score: scoreSchema.nullable(),
    scoredCount: countSchema,
  }),
  /**
   * Every local day of the current window up to `asOf` — a day without a
   * scored record included, with a null score; `scoredCount` sums to
   * `current.scoredCount`.
   */
  days: z.array(dashboardQualityDaySchema),
  /**
   * The six skills, in `DASHBOARD_QUALITY_SKILL_KEYS` order (#1338 PR B) —
   * only when asked (`skills=1`, #2355). Optional: an answer without them still
   * parses — a skills card that gets one shows a source error, the score card
   * is unaffected.
   */
  skills: z
    .array(dashboardQualitySkillSchema)
    .length(DASHBOARD_QUALITY_SKILL_KEYS.length)
    .optional()
    // A skills list this client cannot read (a sub-metric added or renamed on
    // an edge deployed first) drops only the skills, never the whole answer:
    // the score and trend cards keep working.
    .catch(undefined),
  /**
   * The organization's own target for a skill, 0–100 — only when the company
   * set one (`categoryScoreBenchmark`); `null` — none set, and no line is drawn
   * (never a default the company did not choose). A value this client cannot
   * read drops the line only, never the whole answer (as `skills`).
   */
  benchmark: z.number().min(0).max(100).nullable().optional().catch(undefined),
});

/** A skill is compared only with this many scored rows in BOTH windows — the reports' minimum. */
export const QUALITY_SKILL_MIN_SCORED = REPORT_AGENT_MIN_SCORED_CALLS;

/**
 * A skill's change in points to one decimal, `null` — not comparable (fewer
 * than `QUALITY_SKILL_MIN_SCORED` scored rows in either window). The one rule
 * the skills table and «Просели навыки» both read.
 */
export function skillChange(
  skill: z.infer<typeof dashboardQualitySkillSchema>
): number | null {
  const { current, previous } = skill;
  if (
    current.score === null ||
    previous.score === null ||
    current.scoredCount < QUALITY_SKILL_MIN_SCORED ||
    previous.scoredCount < QUALITY_SKILL_MIN_SCORED
  ) {
    return null;
  }
  return Math.round((current.score - previous.score) * 10) / 10;
}

/**
 * «Просели навыки» — the comparable skills that fell (`skillChange` < 0), the
 * steepest first, at most `limit`.
 */
export function droppingSkills(
  skills: ReadonlyArray<z.infer<typeof dashboardQualitySkillSchema>>,
  limit = 2
): Array<{ key: (typeof DASHBOARD_QUALITY_SKILL_KEYS)[number]; change: number }> {
  return skills
    .flatMap((skill) => {
      const change = skillChange(skill);
      return change !== null && change < 0 ? [{ key: skill.key, change }] : [];
    })
    .sort((a, b) => a.change - b.change)
    .slice(0, limit);
}

// ============================================================================
// Types
// ============================================================================

export type DashboardQualityQuery = z.infer<typeof dashboardQualityQuerySchema>;
export type DashboardQualitySliceQuery = z.infer<typeof dashboardQualitySliceQuerySchema>;
export type DashboardQualityScope = z.infer<typeof dashboardQualityScopeSchema>;
export type DashboardQualityDay = z.infer<typeof dashboardQualityDaySchema>;
export type DashboardQualityResponse = z.infer<typeof dashboardQualityResponseSchema>;
export type DashboardQualitySkill = z.infer<typeof dashboardQualitySkillSchema>;
